import { Box, IconButton, InputAdornment, styled, SxProps, Theme, Typography } from '@mui/material';
import { DatePicker as MuiDatePicker, PickersLayout } from '@mui/x-date-pickers-pro';
import CalendarIcon from 'images/icons/calendar-icon.svg?react';
import { Moment } from 'moment';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';
import { calendarDayIn, dateOfCalendarDay, tzMoment, userTimeZone } from 'utils/moment';
import { FieldClearIconButton } from './FieldClearIconButton';

const POPPER_MODIFIERS = [
    { name: 'preventOverflow' as const, options: { rootBoundary: 'viewport' as const, padding: 8 } },
    { name: 'flip' as const, options: { fallbackPlacements: ['bottom', 'top', 'right', 'left'] as const } },
];

// Styled components
interface StyledDatePickerProps {
    iconColor?: string;
    showDefaultError?: boolean;
    showEmptyError?: boolean;
    error?: boolean;
    usesCustomEndAdornment?: boolean;
    hasValue?: boolean;
}

const StyledDatePicker = styled(MuiDatePicker, {
    shouldForwardProp: prop =>
        !['iconColor', 'showDefaultError', 'showEmptyError', 'error', 'usesCustomEndAdornment', 'hasValue'].includes(
            prop as string
        ),
})<StyledDatePickerProps>(
    ({ iconColor, showDefaultError, showEmptyError, error, usesCustomEndAdornment, hasValue }) => ({
        boxSizing: 'border-box',
        width: '100%',
        backgroundColor: getColor(EColors.v3Background),
        borderRadius: '12px',
        '& .MuiInputBase-root > input': {
            fontSize: '.875rem',
            padding: '13.5px',
            ...(usesCustomEndAdornment && {
                paddingRight: hasValue ? '5rem' : '3rem',
            }),
        },
        '& .MuiInputBase-root > .MuiOutlinedInput-notchedOutline': {
            borderRadius: '12px',
            ...(function () {
                if (error) return { borderColor: getColor(EColors.red) };
                if (showDefaultError && showEmptyError) return { borderColor: getColor(EColors.red) };
                if (showDefaultError && !showEmptyError) return {};
                return { border: '1px solid ' + getColor(EColors.v3Border) };
            })(),
        },
        '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
            borderColor: getColor(EColors.v3Dark) + ' !important',
        },
        ...(usesCustomEndAdornment
            ? {
                  '& .MuiInputBase-root .MuiIconButton-root[aria-label="Open calendar"]': {
                      color: iconColor,
                  },
              }
            : {
                  '& .MuiInputBase-root .MuiIconButton-root': {
                      color: iconColor,
                  },
              }),
        '& .MuiInputBase-root': {
            flexDirection: usesCustomEndAdornment ? 'row' : 'row-reverse',
            paddingRight: usesCustomEndAdornment ? '10px' : 0,
        },
        ...(usesCustomEndAdornment && {
            '& .MuiInputAdornment-root': {
                marginLeft: 0,
                marginRight: '2px',
                maxHeight: '100%',
            },
        }),
        '& .MuiFormLabel-root': {
            transform: usesCustomEndAdornment ? 'translate(16px, 14px)' : 'translate(44px, 14px)',
            fontSize: '0.875rem',
            color: getColor(EColors.pureBlack, 0.5),
        },
        '& .MuiFormLabel-root.MuiInputLabel-shrink': {
            color: getColor(EColors.pureBlack, 0.6),
            fontSize: '0.875rem',
            transform: 'translate(9px, -50%) scale(1)',
            backgroundColor: getColor(EColors.pureWhite),
            padding: '0 5px',
            lineHeight: '1em',
        },
    })
);

const StyledPickersLayout = styled(PickersLayout)({
    '.MuiDateCalendar-root .Mui-selected': {
        backgroundColor: getColor(EColors.v3Dark),
    },
    '.MuiDateCalendar-root .MuiPickersDay-root': {
        borderRadius: '4px',
    },
    '& .MuiPickersLayout-actionBar': {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
    },
});

const CalendarHintText = styled('p')({
    fontSize: '0.75rem',
    lineHeight: '16px',
    fontWeight: 500,
    color: getColor(EColors.v3TextSecondary),
    padding: '4px 16px 12px',
    margin: '0 auto',
    fontFamily: '"General Sans", sans-serif',
    width: '100%',
    maxWidth: 280,
    textAlign: 'center',
    overflowWrap: 'break-word',
});

function createLayoutWithHint(calendarHint: ReactNode) {
    function CalendarHintFooter({ className }: { className?: string }) {
        return (
            <Box
                className={className}
                sx={{ display: 'flex', justifyContent: 'center', width: '100%', textAlign: 'center' }}
            >
                <CalendarHintText>{calendarHint}</CalendarHintText>
            </Box>
        );
    }

    const LayoutWithHint = (props: React.ComponentProps<typeof PickersLayout>) => (
        <StyledPickersLayout
            {...props}
            slots={{
                ...props.slots,
                actionBar: CalendarHintFooter,
            }}
        />
    );

    LayoutWithHint.displayName = 'DatePickerLayoutWithHint';
    return LayoutWithHint;
}

// Types
type BaseDatePickerProps = {
    label?: string;
    placeholder?: string;
    error?: FieldError | undefined;
    showDefaultError?: boolean;
    showEmptyError?: boolean;
    value: string | undefined;
    timeZone?: string;
    disabled?: boolean | undefined;
    required?: boolean;
    iconColor?: string;
    testId?: string;
    /** When true, allows keyboard input. Default is false (picker-only). */
    allowKeyboardInput?: boolean;
    sx?: SxProps<Theme>;
    clearable?: boolean;
    /** Optional hint shown below the calendar when the picker popover is open */
    calendarHint?: ReactNode;
    onChange?: (dateText: string) => void;
    onAccept?: (dateText: string) => void;
};

type DatePickerPropsWithRestrictions = BaseDatePickerProps &
    (
        | { disablePast: boolean; disableFuture?: boolean; shouldDisableDate?: (date: Moment) => boolean }
        | { disablePast?: boolean; disableFuture: boolean; shouldDisableDate?: (date: Moment) => boolean }
        | { disablePast?: boolean; disableFuture?: boolean; shouldDisableDate: (date: Moment) => boolean }
        | { disablePast: boolean; disableFuture: boolean; shouldDisableDate?: (date: Moment) => boolean }
        | { disablePast: boolean; disableFuture?: boolean; shouldDisableDate: (date: Moment) => boolean }
        | { disablePast?: boolean; disableFuture: boolean; shouldDisableDate: (date: Moment) => boolean }
        | { disablePast: boolean; disableFuture: boolean; shouldDisableDate: (date: Moment) => boolean }
    );

type DatePickerPropsWithoutRestrictions = BaseDatePickerProps & {
    disablePast?: never;
    disableFuture?: never;
    shouldDisableDate?: never;
};

type DatePickerProps = DatePickerPropsWithRestrictions | DatePickerPropsWithoutRestrictions;

// Helpers
const buildLabelText = (label?: string, required?: boolean): string | undefined => {
    if (!label) return undefined;
    if (required) return `${label} *`;
    return label;
};

const toDateValue = (value: Date | null): string => {
    if (!value) return '';
    if (isNaN(value.getTime())) return '';
    const momentValue = tzMoment(value);
    return momentValue.isValid() ? momentValue.format('YYYY-MM-DD') : '';
};

export const DatePicker = ({
    label,
    placeholder,
    error,
    disabled = false,
    disablePast = false,
    disableFuture = false,
    value: date,
    timeZone = userTimeZone,
    showDefaultError = false,
    showEmptyError = false,
    required = false,
    allowKeyboardInput = false,
    clearable = false,
    sx,
    onChange,
    onAccept,
    shouldDisableDate,
    testId,
    calendarHint,
}: DatePickerProps) => {
    const [open, setOpen] = useState(false);

    const layoutSlot = useMemo(
        () => (calendarHint ? createLayoutWithHint(calendarHint) : StyledPickersLayout),
        [calendarHint]
    );

    const handleChange = useCallback(
        (value: Date | null) => {
            onChange?.(value ? toDateValue(value) : '');
        },
        [onChange]
    );

    const handleAccept = useCallback(
        (value: Date | null) => {
            if (!value) {
                onAccept?.('');
                return;
            }
            if (tzMoment(value).isValid()) {
                onAccept?.(tzMoment(value).format('YYYY-MM-DD'));
            }
        },
        [onAccept]
    );

    const handleClear = useCallback(() => {
        onChange?.('');
        onAccept?.('');
    }, [onChange, onAccept]);

    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => {
        setOpen(false);
        // Blur the text field so it doesn't remain focused after selection
        setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
    }, []);

    const wrappedShouldDisableDate = shouldDisableDate
        ? (d: Date) => shouldDisableDate(calendarDayIn(d, timeZone))
        : undefined;

    const usesCustomEndAdornment = clearable;

    const clearableEndAdornment = usesCustomEndAdornment ? (
        <InputAdornment position="end">
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 0.25 }}>
                <IconButton
                    size="small"
                    onClick={event => {
                        event.stopPropagation();
                        if (!disabled) {
                            setOpen(true);
                        }
                    }}
                    disabled={disabled}
                    edge="end"
                    aria-label="Open calendar"
                    tabIndex={-1}
                >
                    <CalendarIcon />
                </IconButton>
                {date ? (
                    <FieldClearIconButton onClear={handleClear} disabled={disabled} ariaLabel="Clear date" />
                ) : null}
            </Box>
        </InputAdornment>
    ) : undefined;

    return (
        <Box flex={1}>
            <StyledDatePicker
                sx={sx}
                iconColor="transparent"
                usesCustomEndAdornment={usesCustomEndAdornment}
                hasValue={!!date}
                label={buildLabelText(label, required)}
                value={date ? dateOfCalendarDay(date) : null}
                onChange={handleChange}
                onAccept={handleAccept}
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                disabled={disabled}
                disablePast={disablePast}
                disableFuture={disableFuture}
                shouldDisableDate={wrappedShouldDisableDate}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                    openPickerIcon: () => <CalendarIcon />,
                    layout: layoutSlot as typeof PickersLayout,
                }}
                slotProps={{
                    popper: {
                        disablePortal: false,
                        modifiers: POPPER_MODIFIERS,
                        style: { zIndex: 1300 },
                    },
                    ...(usesCustomEndAdornment && {
                        openPickerButton: {
                            sx: { display: 'none' },
                        },
                    }),
                    textField: {
                        placeholder,
                        onClick: () => !disabled && setOpen(true),
                        InputProps: {
                            sx: { cursor: disabled ? 'default' : 'pointer' },
                            ...(usesCustomEndAdornment && { endAdornment: clearableEndAdornment }),
                        },
                        inputProps: {
                            readOnly: !allowKeyboardInput,
                            ...(testId && { 'data-testid': testId }),
                        },
                    },
                }}
                error={!!error}
                showDefaultError={showDefaultError}
                showEmptyError={showEmptyError && !date}
            />

            {!!error && (
                <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                    {error.message}
                </Typography>
            )}
        </Box>
    );
};
